class CodeflowMockupCarousel extends HTMLElement {
  connectedCallback() {
    this._lifecycleController = new AbortController();
    this._mountController = null;
    this._emblaApi = null;
    this._generation = 0;
    this._pollIntervalId = null;

    this._startMountCycle();

    document.addEventListener(
      'shopify:section:load',
      (event) => {
        if (event.target.contains(this)) {
          this._destroy();
          this._startMountCycle();
        }
      },
      { signal: this._lifecycleController.signal }
    );
  }

  disconnectedCallback() {
    this._destroy();
    this._lifecycleController.abort();
  }

  _startMountCycle() {
    const generation = this._generation;
    this._waitForEmbla().then(() => {
      if (generation !== this._generation || !this.isConnected) return;
      this._mount();
    });
  }

  _waitForEmbla() {
    if (window.EmblaCarousel) return Promise.resolve();
    return new Promise((resolve) => {
      this._pollIntervalId = setInterval(() => {
        if (window.EmblaCarousel) {
          clearInterval(this._pollIntervalId);
          this._pollIntervalId = null;
          resolve();
        }
      }, 20);
    });
  }

  _destroy() {
    this._generation += 1;
    if (this._pollIntervalId) {
      clearInterval(this._pollIntervalId);
      this._pollIntervalId = null;
    }
    if (this._mountController) {
      this._mountController.abort();
      this._mountController = null;
    }
    if (this._emblaApi) {
      this._emblaApi.destroy();
      this._emblaApi = null;
    }
  }

  _mount() {
    const viewport = this.querySelector('.mockup-carousel__viewport');
    const slides = Array.from(this.querySelectorAll('.mockup-carousel__slide'));
    if (!viewport || slides.length === 0) return;

    this._mountController = new AbortController();
    const { signal } = this._mountController;

    const prevBtn = this.querySelector('.mockup-carousel__arrow--prev');
    const nextBtn = this.querySelector('.mockup-carousel__arrow--next');
    const dotsContainer = this.querySelector('.mockup-carousel__dots');

    const api = window.EmblaCarousel(viewport, {
      loop: false,
      align: 'start',
      containScroll: 'trimSnaps',
      skipSnaps: false,
    });
    this._emblaApi = api;

    let dots = [];
    if (dotsContainer) {
      dotsContainer.innerHTML = '';
      dots = api.scrollSnapList().map((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'mockup-carousel__dot';
        dot.setAttribute('aria-label', `Ir al slide ${index + 1}`);
        dot.addEventListener('click', () => api.scrollTo(index), { signal });
        dotsContainer.appendChild(dot);
        return dot;
      });
    }

    const updateUI = () => {
      const selected = api.selectedScrollSnap();

      slides.forEach((slide, index) => {
        slide.classList.toggle('is-selected', index === selected);
      });

      dots.forEach((dot, index) => {
        dot.classList.toggle('is-selected', index === selected);
      });

      if (prevBtn) prevBtn.disabled = !api.canScrollPrev();
      if (nextBtn) nextBtn.disabled = !api.canScrollNext();
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', () => api.scrollPrev(), { signal });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => api.scrollNext(), { signal });
    }

    slides.forEach((slide, index) => {
      slide.addEventListener(
        'click',
        (event) => {
          if (index === api.selectedScrollSnap()) return;
          event.preventDefault();
          event.stopPropagation();
          api.scrollTo(index);
        },
        { capture: true, signal }
      );

      if (slide.hasAttribute('tabindex')) {
        slide.addEventListener(
          'keydown',
          (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            api.scrollTo(index);
          },
          { signal }
        );
      }
    });

    api.on('init', updateUI);
    api.on('select', updateUI);
    api.on('reInit', updateUI);
    updateUI();
  }
}

if (!customElements.get('codeflow-mockup-carousel')) {
  customElements.define('codeflow-mockup-carousel', CodeflowMockupCarousel);
}
